/**
 * AskMarcusButton Component
 *
 * Standalone button for summoning Marcus AI to analyze assets, vulnerabilities, or technologies.
 * Triggers navigation to Insights AI Assistant with pre-populated message.
 *
 * Usage:
 * ```tsx
 * <AskMarcusButton onClick={handleSummonMarcus} />
 * ```
 *
 * @module components/actions
 */

import { Bot } from 'lucide-react';

import { Button } from '@/components/Button';
import { useEntitlements } from '@/hooks/useEntitlements';

export interface AskMarcusButtonProps {
  /** Handler for button click */
  onClick: () => void;
  /** Disable the button (default: false) */
  disabled?: boolean;
  /** Test ID for E2E testing */
  'data-testid'?: string;
}

/**
 * AskMarcusButton Component
 *
 * Rounded brand-colored AI button for summoning Marcus.
 * Opens Marcus AI in Insights with context about selected entities.
 *
 * @param props - Component props
 * @returns AskMarcusButton JSX
 *
 * @example
 * ```tsx
 * <AskMarcusButton
 *   onClick={() => summonAurelius({ type: 'asset', keys: ['key1'], displayNames: ['Asset 1'] })}
 *   data-testid="ask-marcus-button"
 * />
 * ```
 */
export function AskMarcusButton({
  onClick,
  disabled = false,
  'data-testid': testId = 'ask-marcus-button',
}: AskMarcusButtonProps) {
  const { canConverse } = useEntitlements();

  if (!canConverse) return null;

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      styleType="primary"
      className="rounded-full"
      size="xxs"
      startIcon={<Bot className="size-4" />}
      data-testid={testId}
      aria-label="Ask Marcus"
    >
      Ask Marcus
    </Button>
  );
}
